import {useState} from 'react';
import {
  Box,
  Menu,
  Typography,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  useTheme,
  Tooltip,
  ListSubheader,
  Button,
} from '@mui/material';
import useLanguage from 'hooks/useLanguage';
import HistoryIcon from '@mui/icons-material/History';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {EMPTY_ID, useGetPlanHistory} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';
import {
  FormSelectionEnum,
  inputMenuActionType,
  methodType,
  SupportedLanguages,
  SWRKeyEnum,
  URLParams,
} from 'interfaces';
import Alert from 'helpers/Alert';
import {type PlanHistory} from 'client';
import {useInputMenuContext} from 'context/InputMenu';
import {useTranslation} from 'react-i18next';

export const AppTopBarPlanHistory = () => {
  const {t} = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const {navigate, planId: currentPlanId, urlSearchParams, pathname} = useParseURL();
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const {language, dateFormatter} = useLanguage();

  const {planId} = useParseURL();
  const {data: history} = useGetPlanHistory({resourceId: planId});
  const theme = useTheme();
  const [hoverVersion, setHoverVersion] = useState<number>();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const makeCurrent = (row: PlanHistory) => {
    if (row.active) {
      return;
    }
    handleClose();
    backendCommunicator
      .updatePlan(row.id, {active: true})
      .then(() => {
        mutate([row.id, SWRKeyEnum.GET_PLAN_HISTORY]);
        mutate([row.id, SWRKeyEnum.GET_PLAN]);
        mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
        if (currentPlanId !== row.id) {
          navigateToPlan(row.id);
        }
      })
      .catch((error) => {
        Alert.error(error);
      });
  };

  const navigateToPlan = (planId: string) => {
    urlSearchParams.delete(URLParams.site);
    urlSearchParams.delete(URLParams.cage);
    urlSearchParams.set(URLParams.plan, planId);
    navigate({
      pathname,
      search: urlSearchParams.toString(),
    });
  };

  const handleClickCopy = (planId: string) => {
    inputMenuDispatch &&
      inputMenuDispatch({
        type: inputMenuActionType.setFormData,
        form: FormSelectionEnum.plan,
        method: methodType.copy,
        formData: {planId},
      });
    handleClose();
  };

  const ITEM_HEIGHT = 42;
  const MAX_ITEMS = 8;
  const PADDING_HEIGHT = 8 * 2;
  const HEADER_HEIGH = 33.5;
  const DIVIDER_HEIGHT = 1;

  const sortByVersion = (first: PlanHistory, second: PlanHistory) => {
    return second.version - first.version;
  };

  if (!history || (history && history.length < 2)) {
    return null;
  }

  return (
    <div>
      <Tooltip title={t('planHistory') ?? ''}>
        <Button onClick={handleClick} variant="outlined" size="small" color="inherit" endIcon={<HistoryIcon />}>
          {t('planHistory')}
        </Button>
      </Tooltip>
      <Menu
        id="demo-customized-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        style={{zIndex: theme.zIndex.appBar + 1}}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * MAX_ITEMS + PADDING_HEIGHT + HEADER_HEIGH + DIVIDER_HEIGHT,
          },
        }}
      >
        <ListSubheader>{t('planHistory')}</ListSubheader>
        <Divider variant="middle" />
        {history &&
          [...history].sort(sortByVersion).map((row) => {
            return (
              <MenuItem
                selected={row.active}
                onClick={() => {
                  makeCurrent(row);
                }}
                key={row.id}
                onMouseEnter={() => {
                  setHoverVersion(row.version);
                }}
                onMouseLeave={() => {
                  setHoverVersion(undefined);
                }}
              >
                <Box mr={theme.spacing(1)} width={language === SupportedLanguages.english ? 110 : 130}>
                  {row.active ? (
                    <Typography>{`# ${row.version} (${t('current')})`}</Typography>
                  ) : (
                    <Typography>{row.version === hoverVersion ? t('makeCurrent') : `# ${row.version}`}</Typography>
                  )}
                </Box>
                <ListItemText sx={{mr: theme.spacing(1)}}>{`${dateFormatter.toLimitedTimeAgo(
                  new Date(row.updatedAt),
                )}`}</ListItemText>
                <Tooltip
                  title={t('copyVersion') ?? ''}
                  placement="right"
                  PopperProps={{
                    style: {zIndex: 3000},
                  }}
                >
                  <IconButton
                    edge="end"
                    color="inherit"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickCopy(row.id);
                    }}
                  >
                    <FileCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            );
          })}
      </Menu>
    </div>
  );
};
